import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'

const TYPING_LINES = [
  { text: 'Hello, World.', delay: 400, color: '#e6edf3' },
  { text: "I'm Seraj Khan.", delay: 900, color: '#00ff41' },
]

const TERMINAL_COMMANDS = [
  { cmd: 'whoami', output: 'seraj_khan // Senior Software Engineer @ BambooBox', delay: 2200 },
  { cmd: 'location --current', output: 'Bengaluru, India | Remote-Friendly', delay: 2700 },
  { cmd: 'expertise --list', output: 'Node.js · AI/LLM · PostgreSQL · Redis · Microservices', delay: 3200 },
  { cmd: 'status', output: '✓ Open to new opportunities', delay: 3700, green: true },
]

function useTypewriter(text, startDelay = 0, speed = 60) {
  const [displayed, setDisplayed] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    setDisplayed('')
    setDone(false)
    let timeout = setTimeout(() => {
      let i = 0
      const interval = setInterval(() => {
        setDisplayed(text.slice(0, i + 1))
        i++
        if (i >= text.length) {
          clearInterval(interval)
          setDone(true)
        }
      }, speed)
      return () => clearInterval(interval)
    }, startDelay)
    return () => clearTimeout(timeout)
  }, [text, startDelay, speed])

  return { displayed, done }
}

function TerminalLine({ cmd, output, startDelay, green }) {
  const [showCmd, setShowCmd] = useState(false)
  const [showOutput, setShowOutput] = useState(false)
  const { displayed: cmdText, done: cmdDone } = useTypewriter(
    showCmd ? `$ ${cmd}` : '',
    0,
    50
  )

  useEffect(() => {
    const t = setTimeout(() => setShowCmd(true), startDelay)
    return () => clearTimeout(t)
  }, [startDelay])

  useEffect(() => {
    if (cmdDone && showCmd) {
      const t = setTimeout(() => setShowOutput(true), 200)
      return () => clearTimeout(t)
    }
  }, [cmdDone, showCmd])

  if (!showCmd) return null

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <span className="text-[#00ff41] font-mono text-sm">
          {cmdText}
          {!cmdDone && <span className="cursor-blink" />}
        </span>
      </div>
      {showOutput && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className={`font-mono text-sm pl-4 mt-0.5 ${green ? 'text-[#00ff41]' : 'text-[#8b949e]'}`}
        >
          → {output}
        </motion.div>
      )}
    </div>
  )
}

export default function Hero() {
  const { displayed: line1, done: line1Done } = useTypewriter(TYPING_LINES[0].text, 400, 70)
  const { displayed: line2 } = useTypewriter(
    line1Done ? TYPING_LINES[1].text : '',
    300,
    70
  )

  const [showTerminal, setShowTerminal] = useState(false)
  const [showCTAs, setShowCTAs] = useState(false)

  useEffect(() => {
    const t1 = setTimeout(() => setShowTerminal(true), 1800)
    const t2 = setTimeout(() => setShowCTAs(true), 4400)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  const scrollTo = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden grid-bg"
    >
      {/* Grid overlay */}
      <div className="absolute inset-0 bg-[#0d1117]/60 pointer-events-none" />


      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Hero Text */}
          <div>
            {/* Greeting line */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-4"
            >
              <span className="font-mono text-[#00ff41] text-sm tracking-widest uppercase">
                &gt; initializing portfolio...
              </span>
            </motion.div>

            {/* Main heading */}
            <div className="mb-6">
              <h1 className="font-mono text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-[#e6edf3]">{line1}</span>
                {!line1Done && <span className="cursor-blink" />}
              </h1>
              {line1Done && (
                <h1 className="font-mono text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mt-2">
                  <span className="text-[#00ff41] glow-green">{line2}</span>
                  {line2.length < TYPING_LINES[1].text.length && (
                    <span className="cursor-blink" />
                  )}
                </h1>
              )}
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              className="text-[#8b949e] text-base sm:text-lg leading-relaxed mb-8 max-w-md"
            >
              Senior Software Engineer with 3.4+ years architecting high-performance
              B2B SaaS platforms. Specialized in Node.js, AI/LLM pipelines, and
              backend systems — 40% faster APIs, 60% faster DB queries, 99% uptime.
            </motion.p>

            {/* CTA Buttons */}
            {showCTAs && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-wrap gap-4 mb-10"
              >
                <motion.button
                  onClick={() => scrollTo('#projects')}
                  className="btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>▶</span> View Work
                </motion.button>
                <motion.button
                  onClick={() => scrollTo('#contact')}
                  className="btn-secondary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>✉</span> Contact Me
                </motion.button>
              </motion.div>
            )}

            {/* Social Links */}
            {showCTAs && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex items-center gap-6"
              >
                {[
                  {
                    label: 'GitHub',
                    href: 'https://github.com/its-seraj',
                    icon: (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                      </svg>
                    ),
                  },
                  {
                    label: 'LinkedIn',
                    href: 'https://www.linkedin.com/in/its-seraj/',
                    icon: (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    ),
                  },
                  {
                    label: 'Twitter',
                    href: 'https://twitter.com/its_seraj',
                    icon: (
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    ),
                  },
                ].map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6e7681] hover:text-[#00ff41] transition-colors duration-200"
                    whileHover={{ scale: 1.2, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </motion.a>
                ))}
                <div className="w-px h-5 bg-[#30363d]" />
                <span className="font-mono text-xs text-[#6e7681]">
                  its-seraj
                </span>
              </motion.div>
            )}
          </div>

          {/* Right: Terminal Window */}
          {showTerminal && (
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="hidden lg:block"
            >
              {/* Terminal Window */}
              <div className="glass-card border border-[#30363d] overflow-hidden">
                {/* Title bar */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[#30363d] bg-[#161b22]">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                    <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                    <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                  </div>
                  <div className="flex-1 text-center">
                    <span className="font-mono text-xs text-[#6e7681]">
                      bash — seraj@portfolio: ~
                    </span>
                  </div>
                </div>

                {/* Terminal body */}
                <div className="p-6 min-h-[280px]">
                  <div className="font-mono text-sm text-[#8b949e] mb-4">
                    Last login: {new Date().toDateString()} on ttys001
                  </div>

                  {TERMINAL_COMMANDS.map((line, i) => (
                    <TerminalLine
                      key={i}
                      cmd={line.cmd}
                      output={line.output}
                      startDelay={line.delay - 2200}
                      green={line.green}
                    />
                  ))}

                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-[#00ff41] font-mono text-sm">$</span>
                    <span className="cursor-blink" />
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                className="absolute -bottom-4 -left-4 glass-card px-3 py-2 border border-[#00ff41]/30"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00ff41]" />
                  <span className="font-mono text-xs text-[#00ff41]">3.4+ years experience</span>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut', delay: 0.5 }}
                className="absolute -top-4 -right-4 glass-card px-3 py-2 border border-[#00d4ff]/30"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00d4ff]" />
                  <span className="font-mono text-xs text-[#00d4ff]">92+ repos on GitHub</span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 5, duration: 0.6 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="font-mono text-xs text-[#6e7681]">scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-0.5 h-8 bg-gradient-to-b from-[#00ff41] to-transparent rounded"
          />
        </motion.div>
      </div>
    </section>
  )
}
